import React from "react";
import { Upload } from "lucide-react";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { Headline, Kpi, PainBanner, Pill, SectionTitle, pct } from "./PersonaUi";
import { EVIDENCE, RIA_BOOK, VERDICT_TONE } from "./personaData";

const ACT = { review: ["REVIEW", "alert"], add: ["CONFIRMING", "good"], watch: ["WATCH", "alert"], hold: ["NO CHANGE", "muted"] };
const ORDER = { review: 0, watch: 1, add: 2, hold: 3 };
const MIX = [["Attractive / Champion", "#34d399", ["add"]], ["No change", "#52525b", ["hold"]], ["Watch", "#fcd34d", ["watch"]], ["Review", "#f59e0b", ["review"]]];

export default function RiaBookXray() {
  const rows = [...RIA_BOOK].sort((a, b) => ORDER[a.act] - ORDER[b.act] || b.w - a.w);
  const tot = RIA_BOOK.reduce((s, r) => s + r.w, 0);
  const review = RIA_BOOK.filter((r) => r.act === "review");
  return (
    <div className="space-y-4" data-testid="persona-ria-xray">
      <PainBanner testid="persona-ria-xray-pain" persona="RIA · BOOK X-RAY"
        pain="I hold 60 names across 120 client accounts — which ones need my attention this week?"
        answer="one ranked list, attention first" />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Headline testid="ria-xray-headline">3 holdings need a review · 2 strengthening · 1 on watch · 6 unchanged</Headline>
          <div className="text-sm text-muted-foreground mt-1">Model book "Core Growth" · 12 of 60 names shown · scored against skilled institutional money, nightly</div>
        </div>
        <Button variant="outline" size="sm" data-testid="ria-xray-upload-btn"><Upload className="w-4 h-4 mr-1" />Upload holdings (CSV / custodian)</Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi testid="ria-kpi-review" label="Needs review" value={review.length} tone="text-amber-400" sub={`${review.reduce((s, r) => s + r.acc, 0)} accounts affected`} />
        <Kpi testid="ria-kpi-confirming" label="Thesis confirming" value="8 / 12" tone="text-emerald-400" sub="pros in profit vs sector" />
        <Kpi testid="ria-kpi-leaving" label="Skilled money leaving" value="2" sub="UNH, GRMN (≥2 skilled exits)" />
        <Kpi testid="ria-kpi-prints" label="Earnings next 14 days" value="7" sub="1 RED setup (GRMN)" />
      </div>
      <Card><CardContent className="pt-4">
        <SectionTitle>Book by verdict (weight)</SectionTitle>
        <div className="flex h-3 rounded overflow-hidden" data-testid="ria-verdict-mix-bar">
          {MIX.map(([l, c, acts]) => {
            const w = RIA_BOOK.filter((r) => acts.includes(r.act)).reduce((s, r) => s + r.w, 0);
            return <div key={l} style={{ width: `${(w / tot) * 100}%`, background: c }} title={`${l} ${((w / tot) * 100).toFixed(0)}%`} />;
          })}
        </div>
        <div className="flex flex-wrap gap-4 mt-2 text-[11px] text-muted-foreground">
          {MIX.map(([l, c, acts]) => <span key={l} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: c }} />{l} · {((RIA_BOOK.filter((r) => acts.includes(r.act)).reduce((s, r) => s + r.w, 0) / tot) * 100).toFixed(0)}%</span>)}
        </div>
      </CardContent></Card>
      <Card><CardContent className="pt-4 overflow-x-auto">
        <table className="w-full text-xs" data-testid="ria-xray-table">
          <thead className="border-b border-border text-muted-foreground">
            <tr>{["Holding", "Weight", "Accounts", "Kensys view", "Pros' thesis", "Skilled flow 2q", "Your cost vs pros' cost", "Next print", "Status"].map((h, i) => (
              <th key={h} className={`py-1.5 px-2 ${i === 0 || i === 3 || i === 8 ? "text-left" : "text-right"}`}>{h}</th>))}</tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const gap = (r.cost / r.basis - 1) * 100;
              return (
                <tr key={r.sym} className={`border-b border-border/40 hover:bg-muted/30 ${r.act === "review" ? "bg-amber-500/5" : ""}`} data-testid={`ria-xray-row-${r.sym}`}>
                  <td className="py-2 px-2 font-mono font-bold">{r.sym}</td>
                  <td className="py-2 px-2 text-right font-mono">{r.w.toFixed(1)}%</td>
                  <td className="py-2 px-2 text-right font-mono text-muted-foreground">{r.acc}</td>
                  <td className={`py-2 px-2 font-semibold ${VERDICT_TONE[r.verdict]}`}>{r.verdict}</td>
                  <td className={`py-2 px-2 text-right font-mono ${r.conf >= 0 ? "text-emerald-400" : "text-red-400"}`}>{pct(r.conf)}</td>
                  <td className={`py-2 px-2 text-right font-mono ${r.flow > 0 ? "text-emerald-400" : r.flow < 0 ? "text-red-400" : "text-muted-foreground"}`}>{r.flow > 0 ? `+${r.flow}` : r.flow} funds</td>
                  <td className="py-2 px-2 text-right font-mono">${r.cost} vs ${r.basis} <span className={gap <= 0 ? "text-emerald-400" : "text-muted-foreground"}>({pct(gap, 0)})</span></td>
                  <td className="py-2 px-2 text-right font-mono">{r.print} {r.earn && <Pill tone={r.earn === "RED" ? "bad" : "good"}>{r.earn}</Pill>}</td>
                  <td className="py-2 px-2"><Pill tone={ACT[r.act][1]}>{ACT[r.act][0]}</Pill></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="text-[10px] text-muted-foreground mt-3">
          "Pros' thesis" = sector-adjusted gain since skilled funds' estimated entry ({EVIDENCE.basis}). "Your cost vs pros' cost" compares your clients' average cost with the institutions' estimated basis. Sample data.
        </div>
      </CardContent></Card>
    </div>
  );
}
